import { create } from "zustand"
import {
	DEFAULT_FREEHAND_HIGHLIGHT_COLOR,
	DEFAULT_FREEHAND_HIGHLIGHT_OPACITY,
	DEFAULT_FREEHAND_HIGHLIGHT_STROKE_WIDTH,
} from "components/ui/customWebviewer/interactions/freehandHighlight.constants"
import {
	DEFAULT_HIGHLIGHT_FILL_COLOR,
	DEFAULT_HIGHLIGHT_LINE_STYLE,
	DEFAULT_HIGHLIGHT_OPACITY,
	DEFAULT_HIGHLIGHT_STROKE_COLOR,
	DEFAULT_HIGHLIGHT_STROKE_WIDTH,
} from "components/ui/customWebviewer/interactions/figureHighlight.constants"

// REQ-400 figure highlight tool state — lives in a store for the same reason splitScreen.store /
// viewerMenus.store do: the toolbar button + the tool-options bar that replaces it are rendered
// inside the Apryse shadow DOM by `applyViewerHeaderAndTools`, whose `render()` callback captures
// its props ONCE (Apryse owns the header lifecycle, and re-roots it on unrelated re-renders — see
// CustomWebViewerHeader's own comment on why `activeTool` is derived live rather than kept in local
// state). A store subscription keeps the header's swapped-toolbar state live without re-registering
// it. `useFigureHighlightCreate` is the shape-tool writer.
export type TFigureShapeType = "rectangle" | "ellipse"

// REQ-400 #1324: the freehand tool is armed through the SAME store, but it is not a figure shape —
// it uses a different Apryse tool, a different endpoint and a different set of options. Keeping one
// store means "some highlight tool is armed" stays a single question (the orchestrator's
// cancel/Escape chains and the tool-options row height both ask it), while `activeShapeTool` and
// `isFreehandArmed` stay separate so neither tool can be confused for the other. They are mutually
// exclusive: arming one disarms the other.

type Store = {
	// null = the shape tool isn't armed (normal toolbar). Set = the header swaps to the tool-options
	// bar and Apryse's native RectangleCreateTool/EllipseCreateTool is armed on every pane.
	activeShapeTool: TFigureShapeType | null
	// The outline and fill the NEXT drawn shape will use (decision 1: chosen before drawing, from the
	// toolbar). Independently settable — the backend's `color` (stroke) and `fill_color` are separate
	// fields (verified via `pnpm run api:show`), matching the two distinct "Color" / "Fill color"
	// buttons the fuller Figma mock shows (node 19019:672061).
	selectedStrokeColor: string
	// `null` = no fill (#1386 review): the fill picker's slashed swatch, which maps straight onto the
	// backend's nullable `fill_color` (null means a transparent fill). Only the FILL is nullable —
	// the stroke picker doesn't offer the option, so `selectedStrokeColor` stays a plain string.
	selectedFillColor: string | null
	// The remaining three controls the fuller mock shows (#1381 review: "the design has much more
	// functionality than implemented") — all independently settable per-request fields the backend
	// already supports (verified via `pnpm run api:show`), so exposing them is pure UI work.
	selectedOpacity: number
	selectedStrokeWidth: number
	selectedLineStyle: string
	// --- freehand (#1324) ---------------------------------------------------------------------
	// false = the freehand tool isn't armed. Its own colour/opacity/width, NOT shared with the shape
	// tool's: the two have different defaults (yellow at 20pt vs red at 2pt) and the design shows
	// each remembering its own, so switching tools must not overwrite the other's settings. There is
	// no fill colour and no line style — an Ink annotation has no fill, and the freehand endpoint
	// accepts neither field.
	isFreehandArmed: boolean
	freehandColor: string
	freehandOpacity: number
	freehandStrokeWidth: number
	armShapeTool: (shape: TFigureShapeType) => void
	disarmShapeTool: () => void
	armFreehandTool: () => void
	disarmFreehandTool: () => void
	setFreehandColor: (color: string) => void
	setFreehandOpacity: (opacity: number) => void
	setFreehandStrokeWidth: (width: number) => void
	setSelectedStrokeColor: (color: string) => void
	setSelectedFillColor: (color: string | null) => void
	setSelectedOpacity: (opacity: number) => void
	setSelectedStrokeWidth: (width: number) => void
	setSelectedLineStyle: (style: string) => void
}

export const useFigureHighlightStore = create<Store>((set) => ({
	activeShapeTool: null,
	selectedStrokeColor: DEFAULT_HIGHLIGHT_STROKE_COLOR,
	selectedFillColor: DEFAULT_HIGHLIGHT_FILL_COLOR,
	selectedOpacity: DEFAULT_HIGHLIGHT_OPACITY,
	selectedStrokeWidth: DEFAULT_HIGHLIGHT_STROKE_WIDTH,
	selectedLineStyle: DEFAULT_HIGHLIGHT_LINE_STYLE,
	isFreehandArmed: false,
	freehandColor: DEFAULT_FREEHAND_HIGHLIGHT_COLOR,
	freehandOpacity: DEFAULT_FREEHAND_HIGHLIGHT_OPACITY,
	freehandStrokeWidth: DEFAULT_FREEHAND_HIGHLIGHT_STROKE_WIDTH,
	// Arming either tool clears the other — they drive different Apryse tools, so leaving both flags
	// set would render two tool-options rows and leave the header's pressed state lying.
	armShapeTool: (shape) => set({ activeShapeTool: shape, isFreehandArmed: false }),
	disarmShapeTool: () => set({ activeShapeTool: null }),
	armFreehandTool: () => set({ isFreehandArmed: true, activeShapeTool: null }),
	disarmFreehandTool: () => set({ isFreehandArmed: false }),
	setFreehandColor: (freehandColor) => set({ freehandColor }),
	setFreehandOpacity: (freehandOpacity) => set({ freehandOpacity }),
	setFreehandStrokeWidth: (freehandStrokeWidth) => set({ freehandStrokeWidth }),
	setSelectedStrokeColor: (selectedStrokeColor) => set({ selectedStrokeColor }),
	setSelectedFillColor: (selectedFillColor) => set({ selectedFillColor }),
	setSelectedOpacity: (selectedOpacity) => set({ selectedOpacity }),
	setSelectedStrokeWidth: (selectedStrokeWidth) => set({ selectedStrokeWidth }),
	setSelectedLineStyle: (selectedLineStyle) => set({ selectedLineStyle }),
}))
